import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography
} from '@mui/material';
import { fetchViews, renameViewAction, saveView } from '../../ducks/actions';
import { getSaveLoading, getCurrentView, getRenameViewApiUrl, getViewsInfo, getViews, getRenameLoading } from '../../ducks/selectors';
import { setCommonAlerts } from 'modules/cbre-flow-Common/actions';
import { SYSTEM_DEFAULT_VIEW_NAME } from 'modules/cbre-flow-view-management/ducks/constants';

interface RenameViewContentProps {
  open: boolean;
  onClose: () => void;
}

const RenameViewContent: React.FC<RenameViewContentProps> = ({ 
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const saveLoading = useSelector(getRenameLoading);
  const currentView = useSelector(getCurrentView);
  const viewInfo = useSelector(getViewsInfo);
  const getAllViews = useSelector(getViews)
  console.log('Current View Info:', viewInfo, currentView);
  const apiUrl = useSelector(getRenameViewApiUrl(viewInfo?.portfolioViewId));
  
  
  const [viewName, setViewName] = useState('');
  const maxLength = 36;
  const textFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      // Focus the text field when dialog opens
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleSave = async () => {
    SYSTEM_DEFAULT_VIEW_NAME

    if (viewName.trim()) {
      if(viewName.toLowerCase()?.includes(SYSTEM_DEFAULT_VIEW_NAME.toLowerCase())){
        dispatch(setCommonAlerts({ message: `You cannot rename a view to "${SYSTEM_DEFAULT_VIEW_NAME}". Please choose a different name.`, type: "failure" }));
        return;
      }
      const isViewNameExists = getAllViews.some((view: any) => (view.viewName).toLowerCase() === (viewInfo?.clientName + ' - ' + viewName.trim()).toLowerCase());
            if(isViewNameExists){
              // Handle duplicate view name error (you can show a message to the user)
              dispatch(setCommonAlerts({ message: "A view with this name already exists. Please choose a different name.", type: "failure" }));
              return;
            }
      // Get current view configuration (this should be passed from the parent component or context)
      const viewData = {
        viewName: viewInfo?.clientName + ' - ' + viewName.trim(),
      };

      await dispatch(renameViewAction({ apiUrl, viewData }));
    }
  };

  const isValid = viewName.trim().length > 0;

  return (
    <Dialog 
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          width: { xs: 'calc(100vw - 32px)', sm: '100%' },
          maxWidth: '500px'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          p: 2,
          fontSize: '18px',
          fontWeight: 600,
          color: '#1A1A1A',
          lineHeight: '24px'
        }}
      >
        Rename view
      </DialogTitle>
      
      <Box sx={{ px: 2, pb: 2 }}>
        <Typography 
          variant="body2" 
          sx={{ mb: 2, fontSize: '16px', lineHeight: '24px', color: '#1A1A1A' }}
        >
          Rename your view with a better name
        </Typography>
        
        <Box sx={{ p: 0 }}>
          <TextField
            fullWidth
            variant="outlined"
            label="View Name"
            value={viewName}
            disabled={saveLoading}
            onChange={(e) => setViewName(e.target.value)}
            placeholder=""
            inputRef={textFieldRef}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                
              }
            }}
            InputProps={{
              startAdornment: (
                <Box
                  component="span"
                  sx={{
                    color: '#666',
                    fontSize: '14px',
                    mr: 0.5,
                    userSelect: 'none',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {viewInfo?.clientName}{' - '}
                </Box>
              ),
            }}
            inputProps={{ 
              maxLength: maxLength,
              style: { fontSize: '14px', lineHeight: '30px' }
            }}
          />
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block',
              textAlign: 'right',
              mt: 0.5,
              fontSize: '12px',
              color: viewName.length > maxLength ? '#d32f2f' : '#888'
            }}
          >
            {viewName.length} / {maxLength}
          </Typography>
        </Box>
      </Box>

      <DialogActions 
        sx={{ 
          px: 2, 
          pb: 3,
          gap: 1,
          justifyContent: 'flex-end'
        }}
      >
        <Button 
          onClick={onClose} 
          disabled={saveLoading}
          variant="outlined"
       
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={!isValid || saveLoading}
         
        >
          {saveLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Saving...
            </Box>
          ) : (
            'Save'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameViewContent;